/*globals globalNavigate, showModalConfirm, showModalError*/
import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import Request from "common/Request";
import { handleWarnings, getFilteredResponse } from "./api";
import Modal from "common/Modal";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import Help from "./Help";
import { safeStr, getPoliciesInRules } from "common/api";
import {
  ActionsRow,
} from "views/Status/Policies/Common";
import useProfile from "common/hooks/useProfile";
import {
  showDynPolicyRate,
  showDynPolicyRateCluster,
} from "views/Config/PolicyEdit/utils";
import View from "common/View";
import Table from "common/graphs/Table/Table";
import {
  NumberCell,
  renderShapeCell,
  TextWithIcon,
} from "common/graphs/Table/TableRenders";

const doesNothing = () => {};
const defaultReturnView = "viewRatePolicies";

const DeleteModalWrapper = styled.div`
  ul {
    padding: 0 0 0 15px;
    margin: -20px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
    }
  }
`;

const FooterMessage = styled.div`
  color: red;
  ul {
    padding: 0 0 0 15px;
    margin: 0px 0 20px 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 20px;

    li {
      margin: 3px 0 !important;
      text-align: left;
    }
  }
`;

const DeleteModal = ({
  showDeleteModal,
  setShowDeleteModal,
  items = [],
  whenDone,
  rules = [],
}) => {
  const { delete: itemsToDelete, notDelete } = items.reduce(
    (acc, item) => {
      if (rules.includes(item.name)) {
        acc.notDelete.push(item);
      } else {
        acc.delete.push(item);
      }
      return acc;
    },
    { delete: [], notDelete: [] }
  );

  const handleApply = () => {
    const cmds = itemsToDelete.map(({ name, source }) =>
      source === "configured" || source === "undefined"
        ? `clear policy rate ${safeStr(name)}`
        : `clear api policy ${safeStr(name)}`
    );
    ifCl
      .run(`${cmds.join("\n")}\n`, /*is batch*/ true)
      .then(handleWarnings)
      .then(() => {
        setShowDeleteModal(false);
        whenDone();
      })
      .catch((error) => showModalError(error, ""));
  };
  if (!showDeleteModal) {
    return null;
  }
  const names = itemsToDelete.map((item) => item.name);
  const title =
    names.length === 1
      ? `Remove "${names[0]}" policy?`
      : `Remove ${names.length} policies?`;
  return (
    <Modal
      title={title}
      onClose={() => setShowDeleteModal(false)}
      closeLabel="CANCEL"
      superIcon={"delete_forever"}
      large={names.length > 1 && names.length < 100}
      content={() => (
        <DeleteModalWrapper>
          {names.length === 1 ? (
            <p>
              WARNING: All policy data will be lost and all subscribers assigned
              to this policy will now have an undefined policy, so they will be
              reassigned to a configured rate policy (normally rate-default).
            </p>
          ) : null}
          {names.length > 1 ? (
            <>
              {names.length < 100 ? (
                <ul>
                  {names.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              ) : null}
              <p>
                WARNING: All policies data will be lost and all subscribers
                assigned to these policies will now have an undefined policy, so
                they will be reassigned to a configured rate policy (normally
                rate-default).
              </p>
            </>
          ) : null}
        </DeleteModalWrapper>
      )}
      applyLabel="YES"
      footerMessage={
        notDelete.length > 0 ? (
          <FooterMessage>
            <p>These policies cannot be deleted, because they are part of a rule.</p>
            <ul>
              {notDelete.map((item, i) => (
                <li key={i}>{item.name}</li>
              ))}
            </ul>
          </FooterMessage>
        ) : null
      }
      onApply={handleApply}
    />
  );
};

export const RatePolicies = ({returnView = defaultReturnView}) => {
  const {
    bqnId,
    clusterStatus: { mode },
    loadClusterStatus,
  } = useContext(BqnContext);
  const actions = useContext(ActionsContext);
  const [request, setRequest] = useState(null);

  const inClusterScope = cmd => 
    mode === "standalone" || bqnId === "all" ? cmd : `${cmd} bqn-id ${bqnId}`;

  const doRequest = () =>
    Promise.all([
      ifCl.run(
        `${[
          inClusterScope("show policy api full"),
          "show config running all",
        ].join("\n")}\n`,
        /*is batch*/ true
      ),
      loadClusterStatus(),
    ]).then(([response]) => {
      const [policiesResponse, configResponse] = response.split(",");
      const rules = getPoliciesInRules(configResponse, "rate");
      return { response: policiesResponse, rules };
    });

  const doLoad = () => {
    setRequest(doRequest());
  };

  useEffect(() => {
    doLoad();
    return actions.recv("do-load", doLoad);
  }, [bqnId, mode]);

  useEffect(() => actions.recv("add-policy", () => addPolicy(returnView)), [returnView]);

  return ( 
    <div className="body table-responsive">
      {request === null ? null : (
        <Request during={request}>
          {(result) => <ShowPolicies returnView={returnView} {...result} />}
        </Request>
      )}
    </div>
  );
};

const ratePoliciesOptions = [
  {label: 'Add Policy', signal: 'add-policy'},
];

const RatePoliciesView = ({options = ratePoliciesOptions, returnView = defaultReturnView}) =>  (
  <View title="Rate Policies" Help={Help} options={options}>
    <ActionsRow doCreatePolicy={() => addPolicy(returnView)} />
    <RatePolicies returnView={returnView} />
  </View>
);

export default RatePoliciesView;

const notFoundRE = /%ERR-ENOENT: Cannot find "(.*)" policy/;

const addPolicy = (returnView) =>
  globalNavigate("viewPolicyRateEdit", {
    returnView,
  });

const openThroughputPerPolicy = (
  policyType = "rate",
  directions = "downSpeed",
  returnView
) => {
  globalNavigate("viewThroughputPerPolicy", {
    policyType,
    directions,
    returnView,
  });
};
/*****************************************************
 * Policies Table
 * ****************************************************/

const configuredOrAssigned = (d) =>
  d === "configured" ? "yes" : d === "assigned" ? "no" : d;

const getColumns = ({
  profile,
  mode,
  bqnId,
  reload = doesNothing,
  returnView,
  setDeleteSelected,
  setShowDeleteModal,
}) => {
  const { hideRates } = profile;
  return [
    ...(mode !== "standalone"
      ? [
          {
            label: "BQN",
            id: "BQN",
            numeric: true,
          },
        ]
      : []),
    {
      label: "POLICY",
      id: "POLICY",
      numeric: false,
      cellClassName: "hyperlink-text",
      onClick: hideRates
        ? null
        : (value, row) =>
            doClickPolicy({
              name: value,
              source: row["ASSIGNMENT"],
              mode,
              returnView,
              bqnId,
            }),
      renderCell: (value, row) => {
        if (hideRates) {
          return value;
        }

        const icon = row["ASSIGNMENT"] === "undefined" ? "warning" : null;
        return (
          <TextWithIcon
            icon={icon}
            value={value}
            className={row["ASSIGNMENT"] === "undefined" ? "col-red" : ""}
          />
        );
      },
    },
    {
      label: "SUBS-PROVISIONED",
      id: "SUBS-PROVISIONED",
      numeric: true,
      cellClassName: "hyperlink-text",
      renderCell: (value) => (
        <NumberCell value={value} precision={0} hasNA={true} />
      ),
      onClick: (value, row) => {
        if (Number(value) > 0) {
          globalNavigate("viewSubscriberAttributes", {
            returnView,
            policy: row["POLICY"],
          });
        }
      },
    },
    {
      label: "SUBS-ACTIVE",
      id: "SUBS-ACTIVE",
      numeric: true,
      cellClassName: "hyperlink-text",
      renderCell: (value) => (
        <NumberCell value={value} precision={0} hasNA={true} />
      ),
      onClick: (value, row) => {
        if (Number(value) > 0) {
          globalNavigate("viewStatusSubscribers", {
            returnView,
            policy: row["POLICY"],
          });
        }
      },
    },
    {
      label: "MBYTES-DOWNLINK",
      id: "MBYTES-DOWNLINK",
      numeric: true,
      cellClassName: "hyperlink-text",
      renderCell: (value) => (
        <NumberCell value={value} precision={3} hasNA={true} />
      ),
      onClick: () => openThroughputPerPolicy("rate", "downSpeed", returnView),
    },
    {
      label: "MBYTES-UPLINK",
      id: "MBYTES-UPLINK",
      numeric: true,
      cellClassName: "hyperlink-text",
      renderCell: (value) => (
        <NumberCell value={value} precision={3} hasNA={true} />
      ),
      onClick: () => openThroughputPerPolicy("rate", "upSpeed", returnView),
    },
    {
      label: "ID",
      id: "ID",
      numeric: false,
    },
    {
      label: "CONFIGURED",
      id: "ASSIGNMENT",
      numeric: false,
      renderCell: configuredOrAssigned,
    },
    {
      label: "BLOCK",
      id: "BLOCK",
      numeric: false,
      hidden: hideRates,
    },
    {
      label: "RATE-LIMIT-DOWN",
      id: "LIMIT-DN",
      numeric: true,
      renderCell: renderShapeCell,
      parse: (data) => {
        if(data === "no" || data === "n/a"){
          return null;
        }
        if (
          typeof data === "string" &&
          data.charAt(data.length - 1) === "%"
        ) {
          return data;
        }
        return parseFloat(data);
      },
      hidden: hideRates,
    },
    {
      label: "RATE-LIMIT-UP",
      id: "LIMIT-UP",
      numeric: true,
      renderCell: renderShapeCell,
      parse: (data) => {
        if(data === "no" || data === "n/a"){
          return null;
        }
        if (
          typeof data === "string" &&
          data.charAt(data.length - 1) === "%"
        ) {
          return data;
        }
        return parseFloat(data);
      },
      hidden: hideRates,
    },
    {
      label: "FLOW-LIMIT",
      id: "FLOW-LIMIT",
      numeric: true,
      renderCell: (value) => (value === "n/a" || value === "no" ? "no" : value),
      parse: (value) => (value === "n/a" || value === "no" ? 0 : parseFloat(value)),
      hidden: hideRates,
    },
    {
      label: "ACM",
      id: "ACM",
      numeric: false,
    },
    {
      label: "ACTIONS",
      id: "ACTIONS",
      actions: [
        {
          label: "Edit",
          icon: (value) => {
            const assignment = value["ASSIGNMENT"];
            return assignment === "assigned" ? "search" : "edit";
          },
          onClick: (value) => {
            doClickPolicy({
              name: value["POLICY"],
              source: value["ASSIGNMENT"],
              mode,
              returnView,
              bqnId,
            });
          },
        },
        {
          label: "Delete",
          icon: "delete",
          onClick: (value, row) => {
            setDeleteSelected([
              { name: value["POLICY"], source: value["ASSIGNMENT"] },
            ]);
            setShowDeleteModal(true);
          },
          hide: (value) => value["POLICY"] === "rate-default",
        },
      ],
    },
  ];
};

export const ShowPolicies = ({ reload, response, rules, returnView }) => {
  const {
    bqns,
    clusterStatus: { mode },
    bqnId,
  } = useContext(BqnContext);
  const actions = useContext(ActionsContext);
  const reloadWhenDone = () => actions.send("do-load");
  const profile = useProfile();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [selected, setSelected] = useState([]);

  if (profile === null) {
    return null;
  }

  const filteredResponse = getFilteredResponse({
    response,
    bqns,
    mode,
    bqnId,
  });

  const dataWithIds = filteredResponse.map((row, index) => ({
    ...row,
    id: row.id !== undefined ? row.id : index
  }));

  const columns = getColumns({
    profile,
    mode,
    bqnId,
    reload: reload || reloadWhenDone,
    returnView,
    setDeleteSelected,
    setShowDeleteModal,
  });

  return (
    <>
      <TableContainer>
        <Table
          isSelectable={true}
          isRowSelectable={false}
          data={dataWithIds}
          sort={true}
          columns={columns}
          search={true}
          csvPrefix="rate-policies"
          rowsPerPageNr={50}
          selected={selected}
          setSelected={setSelected}
          deleteSelected={(values) => {
            const selectedItems = dataWithIds
              .filter((row) => values.includes(row.id))
              .map((row) => ({ name: row["POLICY"], source: row["ASSIGNMENT"] }));
            setDeleteSelected(selectedItems);
            setShowDeleteModal(true);
          }}
        />
      </TableContainer>
      <DeleteModal
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        items={deleteSelected}
        whenDone={() => {
          setSelected([]);
          (reload || reloadWhenDone)();
        }}
        rules={rules}
      />
    </>
  );
};

const doClickPolicy = ({ name, source, mode, returnView, bqnId }) => {
  if (source === "assigned") {
    if (mode === "standalone") {
      showDynPolicyRate(name);
    } else {
      showDynPolicyRateCluster(name, bqnId);
    }
  } else if (source === "configured") {
    globalNavigate("viewPolicyRateEdit", {
      policy: name,
      returnView,
      tab: '2'
    });
  } else if (source === "undefined") {
    doAddPolicy(name, returnView);
  } else {
    return false;
  }
};

const requestPolicy = (policy) => ifCl.run(`show policy ${safeStr(policy)}`);

const confirmCreatePolicy = (policy) =>
  new Promise((resolve, reject) =>
    showModalConfirm(
      `Confirm before continue`,
      `Warning: This policy is undefined, and has been assigned to some subscribers. Those subscribers are now getting a default policy from the configured rules. If you click "Define", you can now define this policy and then the assigned subscribers will get it.`,
      "warning",
      () => resolve(policy),
      () => reject(),
      "DEFINE",
      "RETURN"
    )
  );

const doAddPolicy = (policy, returnView = defaultReturnView) => {
  requestPolicy(policy)
    .then(() => {
      showModalError("Error:", "Already defined policy " + policy);
      throw `Policy already defined ${policy}`;
    })
    .catch((error) => {
      const notFound = error.match(notFoundRE);
      if (notFound !== null) {
        const [, missingName] = notFound;
        return { missing: missingName };
      }
      showModalError("Error:", error);
      throw error;
    })
    .then(confirmCreatePolicy)
    .then(({ missing }) => {
      if (missing !== undefined) {
        globalNavigate("viewPolicyRateEdit", { returnView, create: missing });
      } else {
        console.error("missing policy to create is missing");
      }
    });
};

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
`;
